/**
 * Money on the platform.
 *
 * Every amount — a stake, a balance, a payout, a limit, a chip — is an integer
 * count of its currency's MINOR unit, from the bet to the wallet call and in
 * every database column. USD is counted in cents: a 1 dollar bet is 100, and a
 * 1.95x win on it is 195. Every other currency (IDR, THB, …) is counted in
 * whole units, so its minor unit is the unit itself.
 *
 * Nothing here converts between currencies. It turns the integer into the text
 * a person reads, and text a person typed back into the integer. Every board,
 * the game menu, the round report and the admin format money through this
 * module, so a currency's decimals are decided in exactly one place.
 */

/**
 * Decimals per currency: how many of the stored integer's digits sit after the
 * point. A currency not listed has none.
 *
 * Changing a value re-scales every amount already stored in that currency, so
 * it is never an edit on its own: it ships with a data migration.
 */
export const CURRENCY_DECIMALS: Readonly<Record<string, number>> = Object.freeze({
  USD: 2,
});

/** The currency's decimals: 2 for USD, 0 for every currency counted whole. */
export function currencyDecimals(currency: string | null | undefined): number {
  if (!currency) return 0;
  return CURRENCY_DECIMALS[currency.toUpperCase()] ?? 0;
}

/** Minor units in one major unit: 100n for USD, 1n for a whole-unit currency. */
export function minorPerMajor(currency: string | null | undefined): bigint {
  return 10n ** BigInt(currencyDecimals(currency));
}

const CURRENCY_SYMBOL: Readonly<Record<string, string>> = {
  IDR: 'Rp',
  PLAY_IDR: 'Rp',
  THB: '฿',
  USD: '$',
  USDT: '$',
  VND: '₫',
  PHP: '₱',
};

/** Display symbol for a currency code. An unknown code is shown as itself. */
export function currencySymbol(currency: string | null | undefined): string {
  if (!currency) return '';
  const code = currency.toUpperCase();
  return CURRENCY_SYMBOL[code] ?? code;
}

/** An amount as the wire or a store hands it over: a bigint, a JS number, or
 *  the stringified integer every API sends. Always in minor units. */
export type MoneyInput = bigint | number | string;

/** What an amount that is not an integer prints as. Zero would be a claim
 *  about the player's money; a dash says it is not known. */
export const UNKNOWN_AMOUNT = '—';

function toMinor(v: MoneyInput): bigint | null {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? BigInt(Math.round(v)) : null;
  const s = v.trim();
  return /^-?\d+$/.test(s) ? BigInt(s) : null;
}

function group(n: bigint): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** `abs` minor units with exactly `d` decimals, grouped: 123456 → "1,234.56". */
function exact(abs: bigint, d: number): string {
  if (d === 0) return group(abs);
  const scale = 10n ** BigInt(d);
  return `${group(abs / scale)}.${(abs % scale).toString().padStart(d, '0')}`;
}

/** Like `exact`, but a whole amount drops its decimals: 500 → "5", 250 → "2.50". */
function exactOrWhole(abs: bigint, d: number): string {
  const scale = 10n ** BigInt(d);
  return abs % scale === 0n ? group(abs / scale) : exact(abs, d);
}

/** `abs` minor units in multiples of `div` major units, FLOORED to `digits`
 *  places with trailing zeros dropped. Never rounds up, so never overstates. */
function floored(abs: bigint, scale: bigint, div: bigint, digits: number): string {
  const f = 10n ** BigInt(digits);
  const scaled = (abs * f) / (scale * div);
  const whole = scaled / f;
  const frac = scaled % f;
  if (frac === 0n) return whole.toString();
  return `${whole}.${frac.toString().padStart(digits, '0').replace(/0+$/, '')}`;
}

function signOf(n: bigint, signed: boolean): string {
  return n < 0n ? '-' : signed && n > 0n ? '+' : '';
}

export interface FormatMoneyOptions {
  /** Prefix the currency symbol: "$ 1.95", "Rp 169,000". Default false. */
  symbol?: boolean;
  /** Prefix "+" on a positive amount. A negative always shows "-". Default false. */
  signed?: boolean;
  /** 1,000,000 major units and over print as a floored M/B. Default true. */
  compact?: boolean;
}

/**
 * The canonical amount: balance, bet totals, wins, history, reports.
 *
 * Under a million major units it is exact and grouped, with every decimal the
 * currency has: "169,000" in IDR, "1,000.00" and "1.95" in USD. From a million
 * up it is compact M/B with up to two FLOORED decimals, so it never overstates
 * money: 9,964,105 prints "9.96M", not "10M".
 */
export function formatMoney(
  v: MoneyInput,
  currency: string | null | undefined,
  opts: FormatMoneyOptions = {},
): string {
  const n = toMinor(v);
  if (n === null) return UNKNOWN_AMOUNT;
  const { symbol = false, signed = false, compact = true } = opts;
  const d = currencyDecimals(currency);
  const scale = 10n ** BigInt(d);
  const abs = n < 0n ? -n : n;
  let body: string;
  if (compact && abs >= 1_000_000n * scale) {
    const [div, suffix] = abs >= 1_000_000_000n * scale ? [1_000_000_000n, 'B'] : [1_000_000n, 'M'];
    body = floored(abs, scale, div, 2) + suffix;
  } else {
    body = exact(abs, d);
  }
  const amount = signOf(n, signed) + body;
  const sym = symbol ? currencySymbol(currency) : '';
  return sym ? `${sym} ${amount}` : amount;
}

export interface FormatShortOptions {
  /** Three significant figures (1.25K, 12.3K, 250K) instead of one place
   *  (1.2K, 12.3K, 250K). Default false. */
  adaptive?: boolean;
  /** Prefix "+" on a positive amount. Default false. */
  signed?: boolean;
}

/**
 * An amount in a few characters, for a badge on a bet cell or a button.
 *
 * Under a thousand major units it is exact, and decimals show only when the
 * amount has them: "750", "2.50". From a thousand up it is K/M/B, FLOORED so a
 * badge may read a little under the stake but never over it: 1,999 is "1.9K".
 */
export function formatShort(
  v: MoneyInput,
  currency: string | null | undefined,
  opts: FormatShortOptions = {},
): string {
  const n = toMinor(v);
  if (n === null) return UNKNOWN_AMOUNT;
  const d = currencyDecimals(currency);
  const scale = 10n ** BigInt(d);
  const abs = n < 0n ? -n : n;
  const sign = signOf(n, opts.signed ?? false);
  if (abs < 1_000n * scale) return sign + exactOrWhole(abs, d);
  const [div, suffix] =
    abs >= 1_000_000_000n * scale
      ? [1_000_000_000n, 'B']
      : abs >= 1_000_000n * scale
        ? [1_000_000n, 'M']
        : [1_000n, 'K'];
  let digits = 1;
  if (opts.adaptive) {
    const lead = abs / (scale * div);
    digits = lead < 10n ? 2 : lead < 100n ? 1 : 0;
  }
  return sign + floored(abs, scale, div, digits) + suffix;
}

/**
 * A chip face: "1K", "2.5K", "50K", "1M", "250", and "0.50" for a USD chip
 * worth less than a dollar. A round face is abbreviated; anything else is
 * printed in full so no chip ever reads as a value it is not.
 */
export function formatChip(v: MoneyInput, currency: string | null | undefined): string {
  const n = toMinor(v);
  if (n === null) return UNKNOWN_AMOUNT;
  const d = currencyDecimals(currency);
  const scale = 10n ** BigInt(d);
  const abs = n < 0n ? -n : n;
  const sign = n < 0n ? '-' : '';
  if (abs % scale !== 0n) return sign + exact(abs, d);
  const major = abs / scale;
  if (major >= 1_000_000n && major % 100_000n === 0n) return `${sign}${floored(major, 1n, 1_000_000n, 1)}M`;
  if (major >= 1_000n && major % 100n === 0n) return `${sign}${floored(major, 1n, 1_000n, 1)}K`;
  return sign + group(major);
}

/**
 * The amount as a plain decimal with no grouping: "1234.56" in USD, "1234" in
 * IDR. For export cells and for the value of an input a person edits.
 */
export function toDecimalString(v: MoneyInput, currency: string | null | undefined): string {
  const n = toMinor(v);
  if (n === null) return '';
  const d = currencyDecimals(currency);
  const abs = n < 0n ? -n : n;
  const sign = n < 0n ? '-' : '';
  if (d === 0) return sign + abs.toString();
  const scale = 10n ** BigInt(d);
  return `${sign}${abs / scale}.${(abs % scale).toString().padStart(d, '0')}`;
}

/**
 * The amount in major units as a JS number, for a chart axis or a sort key.
 * Not for arithmetic on money: a float cannot hold every cent of a large sum.
 */
export function toMajorNumber(v: MoneyInput, currency: string | null | undefined): number {
  const n = toMinor(v);
  if (n === null) return Number.NaN;
  return Number(n) / 10 ** currencyDecimals(currency);
}

/**
 * Text a person typed, in major units, as minor units: "1.50" in USD is 150n,
 * "5,000" in IDR is 5000n. Grouping commas are ignored.
 *
 * Returns null for anything that is not a non-negative amount, and for more
 * decimals than the currency has ("1.505" in USD, "10.5" in IDR): rounding a
 * typed amount would change what the person asked for.
 */
export function parseMoney(text: string, currency: string | null | undefined): bigint | null {
  const s = text.trim().replace(/,/g, '');
  const m = /^(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m) return null;
  const whole = m[1] ?? '';
  const frac = m[2] ?? '';
  if (whole.length === 0 && frac.length === 0) return null;
  const d = currencyDecimals(currency);
  if (/[^0]/.test(frac.slice(d))) return null;
  const fracPart = frac.slice(0, d).padEnd(d, '0');
  return BigInt(whole || '0') * 10n ** BigInt(d) + (d > 0 ? BigInt(fracPart) : 0n);
}
